import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Divider, Grid, Container as SContainer } from 'semantic-ui-react';
import { Container } from '@mui/material';
import CustomiseComparableItemMapContainer from 'components/CustomiseComparableItemMapContainer';
import CustomiseComparableListingsContainer from 'components/CustomiseComparableListingsContainer';
import CustomiseComparableMapContainer from 'components/CustomiseComparableMapContainer';
import CustomiseComparableRentalsContainer from 'components/CustomiseComparableRentalsContainer';
import CustomiseComparableSalesContainer from 'components/CustomiseComparableSalesContainer';
import CustomiseCoverLetterContainer from 'components/CustomiseCoverLetterContainer';
import CustomiseCoverPageContainer from 'components/CustomiseCoverPageContainer';
import CustomiseCustomSectionContainer from 'components/CustomiseCustomSectionContainer';
import CustomiseFloorPlanSectionContainer from 'components/CustomiseFloorPlanSectionContainer';
import CustomiseSidePanelContainer from 'components/CustomiseSidePanelContainer';
import CustomiseSuburbProfileContainer from 'components/CustomiseSuburbProfileContainer';
import CustomiseSummaryContainer from 'components/CustomiseSummaryContainer';
import CustomiseTargetPropertyContainer from 'components/CustomiseTargetPropertyContainer';
import CustomiseTestimonialsContainer from 'components/CustomiseTestimonialsContainer';
import FooterGridSpacer from 'components/FooterGridSpacer';
import ReportErrorMessageContainer from 'components/ReportErrorMessage';
import { ScrollToTop } from 'components/ScrollToTop';
import ThemeColourContainer from 'components/ThemeColourContainer';
import connected from '../connected';
import { checkIncluded, sortedSections } from 'ducks/reportSections';
import CustomiseNzTitlesContainer from 'components/CustomiseNzTitlesContainer';
import AnalyticsGateway from 'lib/AnalyticsGateway';
import './CustomiseReportPage.css';
import TargetProperty from 'components/TargetProperty';
import * as Routes from '../Routes';

export const CustomiseReportPage = ({
    comparableSales,
    comparableListings,
    comparableRentals,
    comparableType,
    match,
    featureFlags,
    propertyDetail,
    reportSections,
    reportType,
    userDetails,
}) => {
    const [highlightedSection, setHighlightedSection] = useState(null);
    const sectionsRef = useRef({});
    const sectionPositionsRef = useRef([]);

    const computeOffsetTop = useCallback((el, top = 0) => {
        return el ? computeOffsetTop(el.offsetParent, top + el.offsetTop) : top;
    }, []);

    const parsedPropertyId = useCallback(() => {
        const { params: { propertyId } } = match;
        return parseInt(propertyId, 10);
    }, [match]);

    const onScroll = useCallback(() => {
        if (sectionPositionsRef.current.length) {
            const scrollY = window.scrollY + 40;
            const firstAboveScroll =
                sectionPositionsRef.current.find(({y}) => y <= scrollY) ||
                sectionPositionsRef.current[sectionPositionsRef.current.length - 1];

            if (highlightedSection !== firstAboveScroll.key) {
                setHighlightedSection(firstAboveScroll.key);
            }
        }
    }, [highlightedSection]);

    useEffect(() => {
        const propertyId = parsedPropertyId();
        AnalyticsGateway.sendEvent({
            eventName: 'View Customise Report',
            properties: [
                {label: 'Page', value: 'Customise Report'},
                {label: 'Property ID', value: propertyId}
            ]
        });

        document.addEventListener('scroll', onScroll);

        return () => {
            document.removeEventListener('scroll', onScroll);
        };
    }, [parsedPropertyId, onScroll]);

    useEffect(() => {
        sectionPositionsRef.current = sectionsRef.current ?
            Object.keys(sectionsRef.current)
                .filter(s => sectionsRef.current[s])
                .map(s => ({
                    key: s,
                    y: computeOffsetTop(sectionsRef.current[s]),
                }))
                .sort(({y: y_a}, {y: y_b}) => y_b - y_a)
            : [];
    });

    const renderSection = useCallback((s) => {
        const { floorPlanUris } = propertyDetail;

        switch (s.key) {
            case 'COVER_PAGE':
                return <CustomiseCoverPageContainer/>;
            case 'COVER_LETTER':
                return <CustomiseCoverLetterContainer/>;
            case 'TARGET_PROPERTY':
                return <CustomiseTargetPropertyContainer/>;
            case 'COMPARABLE_MAP':
                return <CustomiseComparableMapContainer/>;
            case 'COMPARABLE_SALES_MAP':
                return <CustomiseComparableItemMapContainer comparableType="sales"/>;
            case 'COMPARABLE_SALES':
                return <CustomiseComparableSalesContainer/>;
            case 'COMPARABLE_LISTINGS_MAP':
                return <CustomiseComparableItemMapContainer comparableType="listings"/>;
            case 'COMPARABLE_LISTINGS':
                return <CustomiseComparableListingsContainer/>;
            case 'COMPARABLE_RENTALS':
                return <CustomiseComparableRentalsContainer/>;
            case 'COMPARABLE_RENTALS_MAP':
                return <CustomiseComparableItemMapContainer comparableType="rentals"/>;
            case 'MARKET_TRENDS':
                return null;
            case 'SUBURB_PROFILE':
                return <CustomiseSuburbProfileContainer/>;
            case 'SUMMARY':
                return <CustomiseSummaryContainer propertyDetail={propertyDetail} userDetails={userDetails}/>;
            case 'DISCLAIMER':
                return null;
            case 'FLOOR_PLAN':
                return <CustomiseFloorPlanSectionContainer floorPlanUris={floorPlanUris}/>;
            case 'TESTIMONIALS':
                return <CustomiseTestimonialsContainer/>;
            case 'NZ_TITLES':
                return <CustomiseNzTitlesContainer/>;
            default:
                return <CustomiseCustomSectionContainer title={s.uiLabel} url={s.url}/>;
        }
    }, [propertyDetail, userDetails]);

    const selectedSections = useCallback(() => {
        const { value: reportTypeValue } = reportType;
        const { floorPlanUris } = propertyDetail;
        return sortedSections(reportSections[reportTypeValue])
            .filter((s) => checkIncluded(s, {comparableSales, comparableListings, comparableRentals}, floorPlanUris));
    }, [reportSections, reportType, comparableSales, comparableListings, comparableRentals, propertyDetail]);

    const showTargetPropertySummary = useCallback(
        (pageContext) => ['Preview Report'].indexOf(pageContext) < 0,
        []
    );

    const renderedSections = useCallback(() => {
        const sections = selectedSections();

        return sections.map((s, idx) => {
            const isLast = idx === sections.length - 1;
            const section = renderSection(s);
            return (
                section &&
                <div key={idx} className="section-detail" ref={(ref) => {
                    sectionsRef.current = {
                        ...sectionsRef.current,
                        [s.url || s.key]: ref,
                    };
                }}>
                    {section}
                    {!isLast && <Divider hidden/>}
                </div>
            );
        });
    }, [selectedSections, renderSection]);

    const focusSection = useCallback((sectionKey) => {
        const section = sectionsRef.current[sectionKey];
        if (section) {
            section.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }, []);

    const { singleLineAddress } = propertyDetail;
    const { pageContext } = Routes.locationInfo(window.location.pathname);

    if (!singleLineAddress) {
        return null;
    }

    return (
        <div className="customise-page">
            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 0 0px #e0e0e0',
                    pt: 2.5,
                    pb: 2.5,
                    mb: '5px',
                }}
            >
                <Container>
                    {showTargetPropertySummary(pageContext) && (
                        <TargetProperty
                            detail={propertyDetail}
                            hideOwnerNamesForVic={featureFlags.hideOwnerNamesForVic}
                            pageContext={pageContext}
                            reportType={reportType}
                            comparableType={comparableType}
                        />
                    )}
                </Container>
            </Container>
            <Container maxWidth="lg">
                <Grid style={{marginLeft: 0}}>
                    <Grid.Row>
                        <Grid.Column width={4} className="sidebar-panel">
                            <CustomiseSidePanelContainer highlightedSection={highlightedSection}
                                                         onFocusSection={focusSection}/>
                        </Grid.Column>
                        <Grid.Column width={12}>
                            <div className="theme-settings">
                                <span className="theme-settings-title">Report Style</span>
                                <ThemeColourContainer/>
                            </div>
                            <SContainer className="sections" style={{marginTop: 50}}>
                                <ReportErrorMessageContainer/>
                                {
                                    renderedSections()
                                }
                            </SContainer>
                        </Grid.Column>
                    </Grid.Row>
                    <FooterGridSpacer/>
                </Grid>
                <ScrollToTop/>
            </Container>
        </div>
    );
};

CustomiseReportPage.propTypes = {
    comparableSales: PropTypes.object.isRequired,
    comparableListings: PropTypes.object.isRequired,
    comparableRentals: PropTypes.object.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            propertyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        }),
    }),
    propertyDetail: PropTypes.object.isRequired,
    reportSections: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                uiLabel: PropTypes.string,
                included: PropTypes.bool,
                sectionData: PropTypes.shape({
                    title: PropTypes.string.isRequired,
                }),
                filename: PropTypes.string,
                url: PropTypes.string,
                index: PropTypes.number,
            })
        )
    ).isRequired,
    reportType: PropTypes.shape({ value: PropTypes.string.isRequired }).isRequired,
    userDetails: PropTypes.object.isRequired,
    comparableType: PropTypes.string.isRequired,
    featureFlags: PropTypes.shape({
        hideOwnerNamesForVic: PropTypes.bool
    }).isRequired,
};

export default connected(CustomiseReportPage);